import React, { useState } from 'react';
import { Age, Code, NamespaceSelect } from './LivePage';
import PagedWorkloadPage from './PagedWorkloadPage';
import { clustersAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

const lbl = (text) => <label style={{ fontSize:12, fontWeight:600, color:'var(--ink-2)', display:'block', marginBottom:4 }}>{text}</label>;

function Usage({ used, hard, keys }) {
  for (const k of keys) {
    if (hard?.[k] !== undefined) {
      return <>{used?.[k] !== undefined ? `${used[k]} / ` : ''}{hard[k]}</>;
    }
  }
  return '—';
}

function CreateQuotaModal({ clusterId, namespaces = [], initialNamespace, onClose, onCreated }) {
  const [namespace, setNamespace] = useState(initialNamespace || namespaces[0] || '');
  const [cpuLimit, setCpuLimit] = useState('');
  const [memoryLimit, setMemoryLimit] = useState('');
  const [podLimit, setPodLimit] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleCreate = async () => {
    if (!namespace) { setError('Namespace is required'); return; }
    if (!cpuLimit && !memoryLimit && !podLimit) {
      setError('At least one of CPU, Memory or Pod limit is required'); return;
    }
    setSaving(true); setError('');
    try {
      await clustersAPI.setResourceQuota(clusterId, namespace, {
        cpu_limit: cpuLimit || undefined,
        memory_limit: memoryLimit || undefined,
        pod_limit: podLimit || undefined,
      });
      onCreated();
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:1000,
      display:'flex', alignItems:'center', justifyContent:'center' }}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="card" style={{ width:480, maxHeight:'90vh', overflowY:'auto' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h2 style={{ margin:0, fontSize:16, fontWeight:700 }}>Create Resource Quota</h2>
          <button onClick={onClose} className="btn btn-ghost btn-icon" style={{ fontSize:20 }}>✕</button>
        </div>
        <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
          <div>
            {lbl('Namespace *')}
            <NamespaceSelect namespaces={namespaces} ns={namespace} onChange={setNamespace} />
          </div>
          <div style={{ fontSize:11.5, color:'var(--muted)' }}>
            Limits apply across every pod in this namespace combined. Leave a field blank to not
            cap that resource. Submitting again replaces the existing quota with these values.
          </div>
          <div>{lbl('CPU limit (e.g. 4, 2000m)')}<input placeholder="e.g. 4" value={cpuLimit} onChange={e=>setCpuLimit(e.target.value)} className="input" /></div>
          <div>{lbl('Memory limit (e.g. 8Gi, 512Mi)')}<input placeholder="e.g. 8Gi" value={memoryLimit} onChange={e=>setMemoryLimit(e.target.value)} className="input" /></div>
          <div>{lbl('Pod count limit')}<input type="number" min={1} placeholder="e.g. 20" value={podLimit} onChange={e=>setPodLimit(e.target.value)} className="input" /></div>
          {error && <div className="banner danger">{error}</div>}
          <div style={{ display:'flex', gap:8, marginTop:4 }}>
            <button onClick={handleCreate} disabled={saving} className="btn btn-primary" style={{ flex:1 }}>
              {saving ? '⟳ Saving...' : '+ Create Quota'}
            </button>
            <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ResourceQuotas() {
  const { hasSectionAction } = useAuth();
  const canDelete = hasSectionAction('workloads', 'delete');
  const [showCreate, setShowCreate] = useState(false);
  const [deletingKey, setDeletingKey] = useState(null);

  const handleDelete = async (clusterId, q, refresh) => {
    const key = `${q.namespace}/${q.name}`;
    if (!window.confirm(`Delete resource quota "${q.name}" in namespace "${q.namespace}"? This removes the cap on that namespace.`)) return;
    setDeletingKey(key);
    try {
      await clustersAPI.deleteResourceQuota(clusterId, q.namespace, q.name);
      refresh();
    } catch (e) {
      alert(`Delete failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setDeletingKey(null);
    }
  };

  return (
    <PagedWorkloadPage
      title="Resource Quotas" kind="ResourceQuota" resource="resourcequotas"
      label="quotas" detail={false}
      createSection="workloads"
      renderCreate={({ namespaces, ns, clusterId, refresh }) => (
        <>
          <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm" style={{ marginLeft:'auto' }}>
            + Create Quota
          </button>
          {showCreate && (
            <CreateQuotaModal
              clusterId={clusterId}
              namespaces={namespaces}
              initialNamespace={ns || namespaces[0]}
              onClose={() => setShowCreate(false)}
              onCreated={refresh}
            />
          )}
        </>
      )}
      cols={['Name', 'Namespace', 'CPU (used/limit)', 'Memory (used/limit)', 'Pods', 'Age', '']}
      renderRow={(q, { clusterId, refresh } = {}) => [
        <td key="n"><Code>{q.name}</Code></td>,
        <td key="ns" style={{ color: 'var(--ink-2)', fontSize: 13 }}>{q.namespace}</td>,
        <td key="c"><Usage used={q.used} hard={q.hard} keys={['limits.cpu', 'requests.cpu', 'cpu']} /></td>,
        <td key="m"><Usage used={q.used} hard={q.hard} keys={['limits.memory', 'requests.memory', 'memory']} /></td>,
        <td key="p"><Usage used={q.used} hard={q.hard} keys={['pods']} /></td>,
        <td key="a"><Age ts={q.created_at} /></td>,
        <td key="actions">
          {canDelete && (
            <button
              className="btn btn-danger"
              style={{ padding: '3px 10px', fontSize: 12 }}
              disabled={deletingKey === `${q.namespace}/${q.name}`}
              onClick={() => handleDelete(clusterId, q, refresh)}
            >
              {deletingKey === `${q.namespace}/${q.name}` ? 'Deleting…' : '🗑️ Delete'}
            </button>
          )}
        </td>,
      ]}
    />
  );
}
